import { Component, Input, computed, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { QuotaDeadline } from '../../../services/dashboard.interface';

interface DeadlineItem {
  info: string;
  days: number;
  passed: boolean;
  progressPercent: number;
}

@Component({
  selector: 'app-quota-deadlines-bars',
  imports: [CommonModule, MatCardModule, TranslateModule],
  templateUrl: './quota-deadlines-bars.html',
  styleUrl: './quota-deadlines-bars.css',
})
export class QuotaDeadlinesBarsComponent {
  private translate = inject(TranslateService);

  @Input() title: string = '';
  @Input() set quotaDeadlines(value: QuotaDeadline[] | null | undefined) {
    this._quotaDeadlines.set(value ?? []);
  }

  private _quotaDeadlines = signal<QuotaDeadline[]>([]);

  items = computed(() => {
    const deadlines = this._quotaDeadlines();
    return deadlines.map(d => ({
      info: d.deadlineInfo ?? '—',
      days: Math.max(0, Number(d.daysUntilDeadline ?? 0)),
      passed: (d.daysUntilDeadline ?? 0) <= 0,
      deadlineDate: d.deadlineDate
    }));
  });

  maxDays = computed(() => {
    const items = this.items();
    const max = Math.max(0, ...items.map(i => i.days));
    return max || 1; // гарантируем ненулевую шкалу
  });

  getProgressPercent(days: number, passed: boolean): number {
    if (passed) return 100;
    const max = this.maxDays();
    const donePct = ((max - days) / max) * 100;
    return Math.min(100, Math.max(0, donePct));
  }

  pluralDays(n: number): string {
    const a = Math.abs(n) % 100;
    const b = a % 10;
    if (a > 10 && a < 20) return this.translate.instant('dashboard.days5plus');
    if (b > 1 && b < 5) return this.translate.instant('dashboard.days2to4');
    if (b === 1) return this.translate.instant('dashboard.day');
    return this.translate.instant('dashboard.days5plus');
  }

  getDeadlinePassedText(): string {
    return this.translate.instant('dashboard.deadlinePassed');
  }

  getDaysRemainingText(): string {
    return this.translate.instant('dashboard.daysRemaining');
  }

  cleanDeadlineInfo(info: string): string {
    // Убираем "— прошёл" из текста
    return info.replace(/\s+—\s*прош[её]л.*$/i, '');
  }
}
